import React from 'react';
import { motion } from 'framer-motion';
import { Network, Info } from 'lucide-react';
import { 
  ALGORITHM_TYPES, 
  getAlgorithmName, 
  getAlgorithmDescription 
} from '../utils/algorithms';

/**
 * AlgorithmSelector component
 * Allows users to select different mesh placement algorithms
 * 
 * @param {Object} props - Component props
 * @param {string} props.selectedAlgorithm - Currently selected algorithm
 * @param {Function} props.onSelectAlgorithm - Function to call when algorithm is selected
 * @returns {JSX.Element} AlgorithmSelector component
 */
const AlgorithmSelector = ({ selectedAlgorithm, onSelectAlgorithm, id }) => { // Added id prop for label association
  // Use the ALGORITHM_TYPES directly from the imported module
  const availableAlgorithms = Object.values(ALGORITHM_TYPES);

  return (
    <div className="mb-6">
      {/* Removed redundant header, assuming parent component provides context */}
      {/* <div className="flex items-center mb-3">...</div> */}

      <div id={id} role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-3"> {/* Use sm breakpoint, added role */}
        {availableAlgorithms.map(algoType => (
          // Use algoType directly as the key, assuming it's unique ('ray_tracing', 'genetic', etc.)
          <motion.div
            key={algoType} // Added key prop
            role="radio" // Accessibility
            aria-checked={selectedAlgorithm === algoType} // Accessibility
            tabIndex={0} // Make it focusable
            className={`border rounded-lg p-3 cursor-pointer transition-all duration-150 ease-in-out ${ // Adjusted padding
              selectedAlgorithm === algoType
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
            }`}
            onClick={() => onSelectAlgorithm(algoType)}
            onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && onSelectAlgorithm(algoType)} // Keyboard accessibility
            whileHover={{ scale: 1.02, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }} // Subtle hover shadow
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-between mb-1"> {/* Reduced margin */}
              <h4 className="font-semibold text-gray-800">{getAlgorithmName(algoType)}</h4> {/* Slightly bolder */}
              {/* Improved selected indicator */}
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedAlgorithm === algoType ? 'bg-purple-600 border-purple-700' : 'border-gray-300'}`}>
                 {selectedAlgorithm === algoType && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
              </div>
            </div>
            <p className="text-xs text-gray-500">{getAlgorithmDescription(algoType)}</p> {/* Smaller description */}
          </motion.div>
        ))}
      </div>
      
      <div className="mt-4 flex items-start bg-blue-50 p-3 rounded-lg border border-blue-100">
        <Info className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium">Algorithm selection directly impacts your mesh node recommendations:</p>
          <ul className="mt-2 space-y-1 ml-4 list-disc">
            <li>Different algorithms may recommend different numbers of mesh nodes for 100% coverage</li>
            <li>More efficient algorithms can achieve the same coverage with fewer nodes</li>
            <li>Placement strategies vary based on the algorithm's approach to signal optimization</li>
          </ul>
          <p className="mt-2">Try different algorithms to see which provides the most efficient solution for your home.</p>
        </div>
      </div>
    </div>
  );
};

export default AlgorithmSelector;